import type { ChecklistTemplate } from '@/types/checklist-template';
import { TaskCategory, TimingAnchor } from '@/types/task';
import { SeasonType } from '@/types/season';

const TEMPLATE_ID = 'builtin-food-plot-annual-midwest';
const BASE_DATE = '2026-01-01T00:00:00.000Z';

const T = {
  soilTesting: 'fp-midwest-001',
  soilTestReview: 'fp-midwest-002',
  orderSeed: 'fp-midwest-003',
  purchaseChemicals: 'fp-midwest-004',
  applyLime: 'fp-midwest-005',
  frostSeedClover: 'fp-midwest-006',
  springFieldPrep: 'fp-midwest-007',
  plantSpringPerennials: 'fp-midwest-008',
  plantWarmSeason: 'fp-midwest-009',
  fertilizeSpring: 'fp-midwest-010',
  mowClover: 'fp-midwest-011',
  summerEval: 'fp-midwest-012',
  sprayForFall: 'fp-midwest-013',
  fallBrassicas: 'fp-midwest-014',
  fallCerealGrains: 'fp-midwest-015',
  fertilizeCereals: 'fp-midwest-016',
  fertilizeBrassicas: 'fp-midwest-017',
  postFrostCheck: 'fp-midwest-018',
  inSeasonDoc: 'fp-midwest-019',
  postSeasonEval: 'fp-midwest-020',
  yearEndReview: 'fp-midwest-021',
} as const;

export const FOOD_PLOT_TEMPLATE: ChecklistTemplate = {
  id: TEMPLATE_ID,
  name: 'Food Plot Annual Cycle — Midwest',
  description:
    'Year-round food plot management cycle. Covers soil testing, planting schedules, maintenance, and evaluation for Midwest food plot programs. Works alongside season-specific checklists.',
  seasonType: SeasonType.Other,
  region: 'midwest',
  isBuiltIn: true,
  version: 1,
  createdAt: BASE_DATE,
  updatedAt: BASE_DATE,
  tasks: [
    // ─── January: Soil testing and ordering ──────────────────────────────────
    {
      id: T.soilTesting,
      templateId: TEMPLATE_ID,
      title: 'Soil testing: all food plot areas',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 1,
      calendarDayRangeStart: 1,
      calendarDayRangeEnd: 15,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['soil_test'],
      sortOrder: 0,
      notes: 'Send samples early — 2-3 week lab turnaround. Test all plot areas. Results drive lime and fertilizer decisions.',
    },
    {
      id: T.soilTestReview,
      templateId: TEMPLATE_ID,
      title: 'Soil test results review: plan amendments',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 1,
      calendarDayRangeStart: 15,
      calendarDayRangeEnd: 31,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [T.soilTesting],
      predefinedActions: [],
      sortOrder: 1,
      notes: 'pH target: 6.5-7.0 for most food plot species. Plan lime and fertilizer rates based on lab recommendations.',
    },
    {
      id: T.orderSeed,
      templateId: TEMPLATE_ID,
      title: 'Order seed (place orders early — shortages common)',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 1,
      calendarDayRangeStart: 1,
      calendarDayRangeEnd: 31,
      zoneAdjustable: false,
      propertyTied: false,
      dependsOn: [],
      predefinedActions: [],
      sortOrder: 2,
      notes: 'Order brassica, cereal, clover, and warm-season seed early. Supply shortages begin in spring.',
    },
    {
      id: T.purchaseChemicals,
      templateId: TEMPLATE_ID,
      title: 'Purchase chemicals/glyphosate (shortages common)',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 1,
      calendarDayRangeStart: 15,
      calendarDayRangeEnd: 28,
      zoneAdjustable: false,
      propertyTied: false,
      dependsOn: [],
      predefinedActions: [],
      sortOrder: 3,
      notes: 'Buy herbicides early before spring rush. Glyphosate shortages are common in planting season.',
    },
    // ─── February: Lime application and frost seeding ─────────────────────────
    {
      id: T.applyLime,
      templateId: TEMPLATE_ID,
      title: 'Apply lime if soil test requires (6+ wks before planting)',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 2,
      calendarDayRangeStart: 1,
      calendarDayRangeEnd: 28,
      zoneAdjustable: true,
      propertyTied: true,
      dependsOn: [T.soilTestReview],
      predefinedActions: ['fertilize'],
      sortOrder: 4,
      notes: 'Lime needs 6+ weeks to adjust pH. Apply early so soil is ready for spring planting.',
    },
    {
      id: T.frostSeedClover,
      templateId: TEMPLATE_ID,
      title: 'Frost-seed clover into existing perennial plots',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 2,
      calendarDayRangeStart: 20,
      calendarDayRangeEnd: 28,
      zoneAdjustable: true,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['frost_seed'],
      sortOrder: 5,
      notes: 'Broadcast clover seed onto frozen/thawing ground. Freeze-thaw cycle pulls seed into soil. Works in light snow.',
    },
    // ─── March: Spring field prep ─────────────────────────────────────────────
    {
      id: T.springFieldPrep,
      templateId: TEMPLATE_ID,
      title: 'Spring field prep: disk, till, or no-till prep',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 3,
      calendarDayRangeStart: 1,
      calendarDayRangeEnd: 31,
      zoneAdjustable: true,
      propertyTied: true,
      dependsOn: [T.applyLime],
      predefinedActions: ['till'],
      sortOrder: 6,
      notes: 'Prepare seedbed for spring planting. Till when soil is workable. No-till option: spray first, seed into stubble.',
    },
    // ─── April-May: Spring planting ───────────────────────────────────────────
    {
      id: T.plantSpringPerennials,
      templateId: TEMPLATE_ID,
      title: 'Plant spring perennials: clover, chicory, alfalfa',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 4,
      calendarDayRangeStart: 15,
      calendarDayRangeEnd: 30,
      zoneAdjustable: true,
      propertyTied: true,
      dependsOn: [T.springFieldPrep],
      predefinedActions: ['plant'],
      sortOrder: 7,
      notes: 'WTI Zone 8 spring window: Apr 1 - Jun 15. Clover, chicory, alfalfa. Firm seedbed required for small seed contact.',
    },
    {
      id: T.plantWarmSeason,
      templateId: TEMPLATE_ID,
      title: 'Plant warm-season annuals: soybeans, buckwheat',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 5,
      calendarDayRangeStart: 15,
      calendarDayRangeEnd: 31,
      zoneAdjustable: true,
      propertyTied: true,
      dependsOn: [T.springFieldPrep],
      predefinedActions: ['plant'],
      sortOrder: 8,
      notes: 'Wait for soil temp 60-65°F. Southern exposure warms first. Soybeans, buckwheat, lab lab for summer browse.',
    },
    // ─── May: Spring fertilization ────────────────────────────────────────────
    {
      id: T.fertilizeSpring,
      templateId: TEMPLATE_ID,
      title: 'Fertilize spring plots (per soil test recommendations)',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 5,
      calendarDayRangeStart: 1,
      calendarDayRangeEnd: 15,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [T.plantSpringPerennials],
      predefinedActions: ['fertilize'],
      sortOrder: 9,
      notes: 'Top-dress established clover with 0-20-20 or per soil test. Nitrogen inhibits clover — use phosphorus and potassium only.',
    },
    // ─── June-July: Maintenance ───────────────────────────────────────────────
    {
      id: T.mowClover,
      templateId: TEMPLATE_ID,
      title: 'Mow clover plots when reaching 8-10 inches',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 6,
      calendarDayRangeStart: 15,
      calendarDayRangeEnd: 30,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [T.plantSpringPerennials],
      predefinedActions: ['mow'],
      sortOrder: 10,
      notes: 'Mow to 4-6 inches when plot reaches 8-10 inches. Prevents overmaturation and weed competition. Promotes fresh growth.',
    },
    {
      id: T.summerEval,
      templateId: TEMPLATE_ID,
      title: 'Summer plot evaluation: trail cam review of plot usage',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 7,
      calendarDayRangeStart: 1,
      calendarDayRangeEnd: 15,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: [],
      sortOrder: 11,
      notes: 'Which plots are getting deer use? Adjust fall planting plan based on real usage data.',
    },
    // ─── July-August: Fall prep ───────────────────────────────────────────────
    {
      id: T.sprayForFall,
      templateId: TEMPLATE_ID,
      title: 'Spray existing plot areas for fall planting (glyphosate)',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 7,
      calendarDayRangeStart: 15,
      calendarDayRangeEnd: 31,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: ['spray'],
      sortOrder: 12,
      notes: 'Need 3 dry days after glyphosate application. Kill existing vegetation before fall plot prep.',
    },
    // ─── August: Fall food plots ──────────────────────────────────────────────
    {
      id: T.fallBrassicas,
      templateId: TEMPLATE_ID,
      title: 'Fall food plot: brassicas (turnips, radishes, kale)',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 8,
      calendarDayRangeStart: 1,
      calendarDayRangeEnd: 15,
      zoneAdjustable: true,
      propertyTied: true,
      dependsOn: [T.sprayForFall],
      predefinedActions: ['till', 'plant'],
      sortOrder: 13,
      notes: 'Zone 8 planting window: Aug 1-15. Peak attraction after first frost when sugars concentrate. Primary late-season draw.',
    },
    {
      id: T.fallCerealGrains,
      templateId: TEMPLATE_ID,
      title: 'Fall food plot: cereal grains (oats, rye, wheat)',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 8,
      calendarDayRangeStart: 15,
      calendarDayRangeEnd: 31,
      zoneAdjustable: true,
      propertyTied: true,
      dependsOn: [T.sprayForFall],
      predefinedActions: ['plant'],
      sortOrder: 14,
      notes: 'Oats, rye, winter wheat. Quick germination — early-season green browse within 2 weeks.',
    },
    // ─── September: Nitrogen top-dress ───────────────────────────────────────
    {
      id: T.fertilizeCereals,
      templateId: TEMPLATE_ID,
      title: 'Fertilize fall plots: nitrogen top-dress for cereals',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 9,
      calendarDayRangeStart: 1,
      calendarDayRangeEnd: 15,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [T.fallCerealGrains],
      predefinedActions: ['fertilize'],
      sortOrder: 15,
      notes: '30-40 lbs N/acre for cereal boost. Apply when plants are 4-6 inches tall. Drives tillering and leaf mass.',
    },
    // ─── October: Brassica nitrogen ───────────────────────────────────────────
    {
      id: T.fertilizeBrassicas,
      templateId: TEMPLATE_ID,
      title: 'Second nitrogen application for brassicas',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 10,
      calendarDayRangeStart: 1,
      calendarDayRangeEnd: 15,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [T.fallBrassicas],
      predefinedActions: ['fertilize'],
      sortOrder: 16,
      notes: 'Boosts bulb and leaf size before frost. Apply urea when brassicas are 6-8 inches tall.',
    },
    // ─── November: In-season evaluation ──────────────────────────────────────
    {
      id: T.postFrostCheck,
      templateId: TEMPLATE_ID,
      title: 'Post-frost brassica palatability check',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 11,
      calendarDayRangeStart: 1,
      calendarDayRangeEnd: 15,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: [],
      sortOrder: 17,
      notes: 'Deer prefer brassicas after first hard frost when starches convert to sugars. Verify deer are using plots.',
    },
    {
      id: T.inSeasonDoc,
      templateId: TEMPLATE_ID,
      title: 'In-season plot usage documentation',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 11,
      calendarDayRangeStart: 1,
      calendarDayRangeEnd: 30,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: [],
      sortOrder: 18,
      notes: 'Document which plots are used, at what times, and by how many deer. Use trail cam evidence.',
    },
    // ─── December: Post-season evaluation ─────────────────────────────────────
    {
      id: T.postSeasonEval,
      templateId: TEMPLATE_ID,
      title: 'Post-season plot evaluation: measure consumption',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 12,
      calendarDayRangeStart: 15,
      calendarDayRangeEnd: 31,
      zoneAdjustable: false,
      propertyTied: true,
      dependsOn: [],
      predefinedActions: [],
      sortOrder: 19,
      notes: 'Walk plots, photo-document, note tonnage consumed. Which species provided value? Which plots had the most activity?',
    },
    {
      id: T.yearEndReview,
      templateId: TEMPLATE_ID,
      title: 'Year-end review: plan next year\'s rotation',
      description: '',
      category: TaskCategory.FoodSource,
      timingAnchor: TimingAnchor.CalendarDate,
      offsetDays: 0,
      calendarMonth: 12,
      calendarDayRangeStart: 15,
      calendarDayRangeEnd: 31,
      zoneAdjustable: false,
      propertyTied: false,
      dependsOn: [T.postSeasonEval],
      predefinedActions: [],
      sortOrder: 20,
      notes: 'What worked? What to change? Species rotation plan to prevent soil depletion and deer habituation.',
    },
  ],
};
